import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Badge,
  Avatar,
  Typography,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  Divider,
  Chip,
  Fade,
  Slide,
  keyframes,
  alpha,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { 
  Dashboard, 
  Logout, 
  People, 
  AccountBalance, 
  Analytics,
  TrendingUp,
  Receipt,
  ContactMail,
  Menu,
  Settings,
  Inventory,
  Favorite,
  Payment,
  Savings,
  WhatsApp,
  Block,
  FactCheck,
  AccountBalanceWallet,
  ShowChart,
  CurrencyExchange,
  CalendarMonth,
  Assessment,
  Speed,
  Timeline,
  CandlestickChart,
  Groups,
  Hub,
} from "@mui/icons-material";
import { useCookies } from "react-cookie";
import { useNavigate, useLocation } from "react-router-dom";
import { ReleasepilotAffiliation } from "../branding/ReleasepilotAffiliation";
import { useBusinessDomain } from "../../contexts/BusinessDomainContext";
import {
  BUSINESS_DOMAINS,
  groupedNavItems,
  type AdminNavItem,
  type BusinessDomain,
} from "../../config/BusinessDomainConfig";

// Custom animations for Navbar component
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.pathname);

  const [, , removeCookie] = useCookies(["auth_token"]);
  const logout = () => {
    let date = new Date();
    date.setDate(1);
    date.setMonth(1);
    date.setFullYear(1960);
    removeCookie("auth_token", { path: "/", expires: date });
    navigate("/");
  };

  const navigationItems = [
    {
      id: '/customer/dashboard',
      label: 'Dashboard',
      icon: <Dashboard />,
      color: '#4CAF50',
      subtext: 'My Overview'
    },
    {
      id: '/customer/loans',
      label: 'My Loans',
      icon: <AccountBalance />,
      color: '#2196F3',
      subtext: 'Loan Management'
    }
  ];

  return (
    <AppBar
      position="static"
      sx={{ 
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        borderRadius: "0 0 20px 20px",
        mx: 2,
        mt: 1,
        width: "calc(100% - 32px)"
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ 
              background: "linear-gradient(45deg, #667eea, #764ba2)",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)"
            }}>
              💸
            </Avatar>
            <Fade in={true} timeout={1000}>
              <Typography variant="h6" sx={{ 
                color: "white", 
                fontWeight: "bold",
                background: "linear-gradient(45deg, #fff, #f0f0f0)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text"
              }}>
                Loan Portal
              </Typography>
            </Fade>
          </Box>

          <List sx={{ display: "flex", gap: 1 }}>
            {navigationItems.map((item, index) => {
              const isActive = activeTab === item.id;
              return (
                <ListItem key={item.id} disablePadding>
                  <Tooltip title={item.label} placement="bottom">
                    <ListItemButton
                      onClick={() => {
                        setActiveTab(item.id);
                        navigate(item.id);
                      }}
                      sx={{
                        borderRadius: "15px",
                        mx: 0.5,
                        background: isActive ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.05)",
                        backdropFilter: "blur(10px)",
                        border: isActive ? "2px solid rgba(255,255,255,0.3)" : "2px solid transparent",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform: isActive ? "scale(1.05)" : "scale(1)",
                        position: "relative",
                        overflow: "hidden",
                        "&:hover": {
                          background: "rgba(255,255,255,0.15)",
                          transform: "scale(1.08)",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                          transition: "left 0.5s",
                        },
                        "&:hover::before": {
                          left: "100%",
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: isActive ? "white" : "rgba(255,255,255,0.9)",
                        transition: "all 0.3s ease",
                        transform: isActive ? "scale(1.2)" : "scale(1)"
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      <Slide direction="right" in={isActive} timeout={300}>
                        <ListItemText 
                          primary={item.label}
                          sx={{ 
                            color: "white",
                            "& .MuiListItemText-primary": {
                              fontSize: "0.95rem",
                              fontWeight: isActive ? "bold" : "medium"
                            }
                          }}
                        />
                      </Slide>
                      {isActive && (
                        <Chip 
                          label="Active" 
                          size="small" 
                          sx={{ 
                            background: "rgba(255,255,255,0.2)",
                            color: "white",
                            border: "1px solid rgba(255,255,255,0.3)",
                            ml: 1,
                            animation: `${pulse} 2s infinite`
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Badge badgeContent={3} color="error" sx={{ "& .MuiBadge-badge": { animation: `${pulse} 2s infinite` } }}>
            <ListItemButton 
              onClick={logout}
              sx={{
                borderRadius: "15px",
                background: "rgba(255,255,255,0.1)",
                border: "2px solid rgba(255,255,255,0.2)",
                color: "white",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "linear-gradient(45deg, #ff6b6b, #ff8e8e)",
                  transform: "scale(1.05)",
                  boxShadow: "0 8px 25px rgba(255,107,107,0.3)"
                }
              }}
            >
              <Logout sx={{ mr: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                Logout
              </Typography>
            </ListItemButton>
          </Badge>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

const AdminNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(location.pathname);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { domain, setDomain } = useBusinessDomain();

  useEffect(() => {
    setActiveTab(location.pathname);
  }, [location.pathname]);

  const [, , removeCookie] = useCookies(["auth_token"]);
  const logout = () => {
    let date = new Date();
    date.setDate(1);
    date.setMonth(1);
    date.setFullYear(1960);
    removeCookie("auth_token", { path: "/", expires: date });
    navigate("/");
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const adminNavigationGroups = groupedNavItems(domain);

  const renderNavIcon = (item: AdminNavItem) => {
    const icons: Record<string, React.ReactNode> = {
      Analytics: <Analytics />,
      People: <People />,
      Block: <Block />,
      FactCheck: <FactCheck />,
      AccountBalanceWallet: <AccountBalanceWallet />,
      CurrencyExchange: <CurrencyExchange />,
      ShowChart: <ShowChart />,
      CandlestickChart: <CandlestickChart />,
      Groups: <Groups />,
      Hub: <Hub />,
      Savings: <Savings />,
      WhatsApp: <WhatsApp />,
      ContactMail: <ContactMail />,
      Inventory: <Inventory />,
      Payment: <Payment />,
      CalendarMonth: <CalendarMonth />,
      Assessment: <Assessment />,
      Speed: <Speed />,
      Timeline: <Timeline />,
      AccountBalance: <AccountBalance />,
      Receipt: <Receipt />,
      TrendingUp: <TrendingUp />,
      Favorite: <Favorite />,
      Settings: <Settings />,
    };
    return icons[item.iconName] ?? <Dashboard />;
  };

  const handleNavigation = (itemId: string) => {
    setActiveTab(itemId);
    navigate(itemId);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawerWidth = 268;

  // Drawer content component
  const drawerContent = (
    <Box sx={{ width: drawerWidth, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            width: 40,
            height: 40,
            fontFamily: '"Fraunces", serif',
            fontWeight: 650,
            fontSize: '1.1rem',
          }}
        >
          W
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Fraunces", serif',
              fontWeight: 650,
              lineHeight: 1.2,
              fontSize: '1.15rem',
            }}
          >
            Whatzneu
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Admin console
          </Typography>
          <ReleasepilotAffiliation compact variant="onLight" sx={{ mt: 0.75 }} />
        </Box>
      </Box>
      <Divider />
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="overline" sx={{ display: 'block', mb: 0.75 }}>
          Domain
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={domain}
            onChange={(e) => setDomain(e.target.value as BusinessDomain)}
            displayEmpty
          >
            {BUSINESS_DOMAINS.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {BUSINESS_DOMAINS.find((d) => d.id === domain)?.description}
        </Typography>
      </Box>
      <Divider />
      <List sx={{ pt: 1, flex: 1, overflow: 'auto', minHeight: 0, px: 0.5 }}>
        {adminNavigationGroups.map(({ group, items }) => (
          <Box key={group} sx={{ mb: 1.25 }}>
            <Typography
              variant="overline"
              sx={{ px: 2, pt: 1, pb: 0.5, display: 'block', color: 'text.secondary' }}
            >
              {group}
            </Typography>
            {items.map((item) => {
              const isActive = activeTab === item.id || activeTab.startsWith(`${item.id}/`);
              return (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.id)}
                    selected={isActive}
                    sx={{
                      mx: 1,
                      mb: 0.25,
                      borderRadius: 1.5,
                      py: 0.85,
                      position: 'relative',
                      transition: 'background-color 0.15s ease, color 0.15s ease',
                      '&.Mui-selected': {
                        backgroundColor: (t) => `${t.palette.primary.main}14`,
                        color: 'primary.dark',
                        '&:hover': {
                          backgroundColor: (t) => `${t.palette.primary.main}22`,
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'primary.main',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 8,
                          bottom: 8,
                          width: 3,
                          borderRadius: 2,
                          bgcolor: 'primary.main',
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive ? 'primary.main' : 'text.secondary',
                        minWidth: 36,
                      }}
                    >
                      {renderNavIcon(item)}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      secondary={isMobile ? item.subLabel : undefined}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 700 : 500,
                        fontSize: '0.875rem',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </Box>
        ))}
      </List>
      <Divider sx={{ flexShrink: 0 }} />
      <Box sx={{ px: 2, py: 2, flexShrink: 0 }}>
        <ListItemButton
          onClick={logout}
          sx={{
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'error.light',
              color: 'error.main',
              bgcolor: 'error.50',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  const drawerPaperSx = {
    boxSizing: 'border-box',
    width: drawerWidth,
    borderRight: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  };

  return (
    <>
      {/* Mobile: thin top bar with menu + logo + logout */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            display: { xs: 'block', md: 'none' },
            backgroundColor: '#ffffff',
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', minHeight: 56, px: 1.5 }}>
            <IconButton
              color="inherit"
              aria-label="menu"
              onClick={handleDrawerToggle}
              sx={{ color: theme.palette.text.primary }}
            >
              <Menu />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                📊
              </Avatar>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Admin</Typography>
            </Box>
            <IconButton
              onClick={logout}
              sx={{ color: theme.palette.error.main }}
            >
              <Logout />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {/* Desktop: permanent left drawer (in document flow — no fixed offset spacer needed) */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            ...drawerPaperSx,
            position: 'relative',
            height: '100%',
            minHeight: '100vh',
            overflowY: 'auto',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Mobile: temporary left drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': drawerPaperSx,
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

/** Width of the admin left sidebar (for layout offset). */
export const ADMIN_DRAWER_WIDTH = 280;

export { Navbar, AdminNavbar };
